import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import XIcon from "@untitled-ui/icons-react/build/esm/X";
import { useEffect, useMemo, useState, type FC } from "react";
import { Dialog } from "~/components/dialog";
import { Box, Button, Card, CardContent, CardHeader, Grid, IconButton, Popover } from "@mui/material";
import { DATA_DISPLAY_CHANGE_TYPE, DataDisplayFile, DataDisplayState, getFileCustomFieldDisplayId, ISection } from "~/contexts/data-display-context/data-display-context";
import { sortBySortingIndex } from "~/utils/sorting-array-result";
import { useGetFileCustomFields } from "~/queries/useGetCustomFields";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { DISPLAY_PREFERENCE_SECTION_DISPLAY } from "~/types/__generated/gql/graphql";
import toast from "react-hot-toast";
import { fromKababCaseToTitleCase } from "~/utils/string-util";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { usePopover } from "~/hooks/use-popover";
interface FileDisplaySettingsDialogProps {
  file: DataDisplayFile;
  changeFileDisplayFormSettings: (type: DATA_DISPLAY_CHANGE_TYPE, change: ISection[]) => Promise<void>;
  onClose: () => void;
}
const getFileDropLocationId = (rowSortingIndex: number | null, fieldId: string | null) => `file-drop-${JSON.stringify([rowSortingIndex, fieldId])}`;
export const FileDisplaySettingsDialog: FC<FileDisplaySettingsDialogProps> = props => {
  const {
    file,
    changeFileDisplayFormSettings,
    onClose
  } = props;
  const {
    data: customFields = []
  } = useGetFileCustomFields();
  const customFieldLabels = new Map(customFields.map(field => [getFileCustomFieldDisplayId(field._id), field.name]));
  const [sections, setSections] = useState<DataDisplayState["file"]["form"]["sections"] | undefined>();
  useEffect(() => {
    if (file.form.sections) {
      setSections(file.form.sections);
    }
  }, [file]);
  const handleSetFormState = (sectionIndex: number, change: DataDisplayState["file"]["form"]["sections"][0]["rows"]) => {
    const newSections = (sections ?? []).map((section, index) => sectionIndex === index ? {
      ...section,
      rows: change
    } : section);
    return setSections(newSections);
  };
  const handleVisibilityChange = (sectionIndex: number, rowIndex: number, index: number, update: boolean) => {
    const state = Array.from(sections ?? []);
    // state[sectionIndex].rows[rowIndex].fields[index].isVisible = update;
    return setSections(state.map(section => {
      if (section.sortingIndex !== sectionIndex) return section;
      return {
        ...section,
        rows: section.rows.map(row => {
          if (row.sortingIndex !== rowIndex) return row;
          return {
            ...row,
            fields: row.fields.map(field => {
              if (field.sortingIndex !== index) return field;
              return {
                ...field,
                isVisible: update
              };
            })
          };
        })
      };
    }));
  };
  const handleAddEmptyRow = (sectionIndex: number, rowIndex: number, isAddAbove: boolean) => {
    const state = Array.from(sections ?? []);
    const newState = state.map(section => {
      if (section.sortingIndex !== sectionIndex) return section;
      return {
        ...section,
        rows: section.rows.map(row => {
          if (row.sortingIndex === rowIndex) {
            if (isAddAbove) {
              return [{
                sortingIndex: row.sortingIndex,
                fields: []
              }, {
                ...row,
                sortingIndex: row.sortingIndex + 1
              }];
            }
            return [row, {
              sortingIndex: row.sortingIndex + 1,
              fields: []
            }];
          }
          if (row.sortingIndex > rowIndex) {
            return [{
              ...row,
              sortingIndex: row.sortingIndex + 1
            }];
          }
          return [row];
        }).flat()
      };
    }).sort(sortBySortingIndex);
    return setSections(newState);
  };
  const onSave = async () => {
    try {
      if (!sections) throw new Error("Form configuration not found.");
      await changeFileDisplayFormSettings(DATA_DISPLAY_CHANGE_TYPE.SECTIONS, sections);
      onClose();
    } catch {
      toast.error("Failed to save file display settings");
    }
  };
  return <Dialog dialogProps={{
    maxWidth: "xl"
  }} title={<Stack alignItems="center" direction="row" justifyContent="space-between" spacing={3}>
					<Typography variant="h6">File Display Settings</Typography>
					<IconButton color="inherit" onClick={onClose}>
						<SvgIcon>
							<XIcon />
						</SvgIcon>
					</IconButton>
				</Stack>} content={<Stack alignItems="center" direction="column" justifyContent="space-between" spacing={3}>
					{(sections ?? []).map((section, index) => <FileForm key={`form-${section.sortingIndex}`} forms={section.rows} sectionDisplay={section.display} sectionName={section.name} sectionIndex={index} handleSetFormState={handleSetFormState} handleVisibilityChange={handleVisibilityChange} handleAddEmptyRow={handleAddEmptyRow} fieldLabels={customFieldLabels} />)}
				</Stack>} AfterContent={<Box sx={{
    display: "flex"
  }} gap={2}>
					<Grid alignContent="center" alignItems="center" spacing={2} container>
						<Grid xs={12} item>
							<Box sx={{
          display: "flex",
          flexDirection: "row-reverse",
          my: 2
        }} gap={2}>
								<Button variant="contained" onClick={event => {
            event.preventDefault();
            void onSave();
          }}>
									Save
								</Button>
								<Button variant="outlined" onClick={onClose}>
									Cancel
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Box>} data-sentry-element="Dialog" data-sentry-component="FileDisplaySettingsDialog" data-sentry-source-file="file-display-settings-dialog.tsx" />;
};
interface FileFormProps {
  forms: DataDisplayState["file"]["form"]["sections"][0]["rows"];
  sectionDisplay: DataDisplayState["file"]["form"]["sections"][0]["display"];
  sectionName: string;
  sectionIndex: number;
  handleSetFormState: (sectionIndex: number, change: DataDisplayState["file"]["form"]["sections"][0]["rows"]) => void;
  handleVisibilityChange: (sectionIndex: number, rowIndex: number, index: number, update: boolean) => void;
  handleAddEmptyRow: (sectionIndex: number, rowIndex: number, isAddAbove: boolean) => void;
  fieldLabels: Map<string, string>;
}
const FileForm: FC<FileFormProps> = props => {
  const {
    forms,
    sectionDisplay,
    sectionName,
    sectionIndex,
    handleSetFormState,
    handleVisibilityChange,
    handleAddEmptyRow,
    fieldLabels
  } = props;
  const formFields: DataDisplayState["file"]["form"]["sections"][0]["rows"] = useMemo(() => {
    return forms.sort(sortBySortingIndex).map(form => ({
      sortingIndex: form.sortingIndex,
      fields: form.fields
    }));
  }, [forms]);
  const handleDragEndFile = (result: DropResult): void => {
    const {
      source,
      destination
    } = result;
    if (!destination) return;
    const rows = formFields.map(row => ({
      ...row,
      fields: [...row.fields]
    }));
    const sourceRow = rows.find(row => row.fields.some(field => getFileDropLocationId(row.sortingIndex, field.id) === source.droppableId));
    const sourceFieldIndex = sourceRow?.fields.findIndex(field => getFileDropLocationId(sourceRow.sortingIndex, field.id) === source.droppableId);
    if (!sourceRow || sourceFieldIndex === undefined || sourceFieldIndex < 0) {
      return;
    }
    const sourceField = sourceRow.fields[sourceFieldIndex];
    const destinationRow = rows.find(row => getFileDropLocationId(row.sortingIndex, null) === destination.droppableId || row.fields.some(field => getFileDropLocationId(row.sortingIndex, field.id) === destination.droppableId));
    const isNewRowDrop = destination.droppableId === getFileDropLocationId(null, null);
    if (!destinationRow && !isNewRowDrop) return;
    const destinationField = destinationRow?.fields.find(field => getFileDropLocationId(destinationRow.sortingIndex, field.id) === destination.droppableId);
    if (destinationRow === sourceRow) {
      if (!destinationField) {
        sourceRow.fields.splice(sourceFieldIndex, 1);
        sourceRow.fields.push(sourceField);
      } else {
        if (destinationField.id === sourceField.id) return;
        sourceRow.fields.splice(sourceFieldIndex, 1);
        const targetIndex = sourceRow.fields.findIndex(field => field.id === destinationField.id);
        if (targetIndex === -1) return;
        sourceRow.fields.splice(targetIndex, 0, sourceField);
      }
    } else if (destinationRow && destinationField) {
      const destinationFieldIndex = destinationRow.fields.findIndex(field => field.id === destinationField.id);
      if (destinationFieldIndex === -1) return;
      if (destinationRow.fields.length < 4) {
        sourceRow.fields.splice(sourceFieldIndex, 1);
        destinationRow.fields.splice(destinationFieldIndex, 0, sourceField);
      } else {
        destinationRow.fields[destinationFieldIndex] = sourceField;
        sourceRow.fields[sourceFieldIndex] = destinationField;
      }
    } else if (destinationRow) {
      sourceRow.fields.splice(sourceFieldIndex, 1);
      destinationRow.fields.push(sourceField);
    } else if (isNewRowDrop) {
      sourceRow.fields.splice(sourceFieldIndex, 1);
      rows.push({
        sortingIndex: Math.max(-1, ...rows.map(row => row.sortingIndex)) + 1,
        fields: [sourceField]
      });
    }
    let fieldIndex = 0;
    const newArr = rows.filter(form => form.fields.length > 0).sort(sortBySortingIndex).map((form, index) => ({
      ...form,
      sortingIndex: index,
      fields: form.fields.map(field => ({
        ...field,
        sortingIndex: fieldIndex++
      }))
    }));
    void handleSetFormState(sectionIndex, newArr as DataDisplayState["file"]["form"]["sections"][0]["rows"]);
  };
  return <Card sx={{
    width: "100%"
  }} data-sentry-element="Card" data-sentry-component="FileForm" data-sentry-source-file="file-display-settings-dialog.tsx">
			<CardHeader title={sectionName} data-sentry-element="CardHeader" data-sentry-source-file="file-display-settings-dialog.tsx" />
			<CardContent sx={{
      pt: 2
    }} data-sentry-element="CardContent" data-sentry-source-file="file-display-settings-dialog.tsx">
				<DragDropContext onDragEnd={handleDragEndFile} data-sentry-element="DragDropContext" data-sentry-source-file="file-display-settings-dialog.tsx">
					{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      @ts-ignore */}
					<Grid container data-sentry-element="Grid" data-sentry-source-file="file-display-settings-dialog.tsx">
						{formFields.map((form, rowIndex) => <Grid key={`grid-form-service-${rowIndex}`} xs={12} item>
								<Box>
									<Grid container p={2} my={1} sx={{
                backgroundColor: theme => theme.palette.mode === "dark" ? "neutral.700" : "neutral.100",
                borderRadius: 1
              }}>
										{form.fields.map(field => {
                  const length = form.fields.length < 4 ? form.fields.length + 1 : form.fields.length;
                  const isGridDisplay = sectionDisplay === DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID;
                  const gridSize = isGridDisplay ? 12 / length : 12;
                  return <FileDraggableComponent key={`file-form-${field.sortingIndex}`} sectionIndex={sectionIndex} rowIndex={rowIndex} rowSortingIndex={form.sortingIndex} index={field.sortingIndex} grid={gridSize} formField={field} label={fieldLabels.get(field.id)} handleVisibilityChange={handleVisibilityChange} />;
                })}
										{sectionDisplay === DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID && form.fields.length < 4 && <FileDraggableComponent key={`file-form-extra-${(form.fields[form.fields.length - 1]?.sortingIndex ?? -1) + 1}`} sectionIndex={sectionIndex} rowIndex={rowIndex} rowSortingIndex={form.sortingIndex} index={(form.fields[form.fields.length - 1]?.sortingIndex ?? -1) + 1} grid={1} handleAddEmptyRow={handleAddEmptyRow} />}
									</Grid>
								</Box>
							</Grid>)}
						{sectionDisplay === DISPLAY_PREFERENCE_SECTION_DISPLAY.GRID && <Grid key={`grid-form-extra-d-${formFields.length}`} xs={12} item>
								<Box>
									<Grid container p={2} my={1} sx={{
                backgroundColor: theme => theme.palette.mode === "dark" ? "neutral.700" : "neutral.100",
                borderRadius: 1
              }}>
										<FileDraggableComponent key={`file-form-extra-dc-${formFields.length}`} sectionIndex={sectionIndex} rowIndex={formFields.length} rowSortingIndex={null} index={formFields.map(form => form.fields).flat().length} grid={12} />
									</Grid>
								</Box>
							</Grid>}
					</Grid>
				</DragDropContext>
			</CardContent>
		</Card>;
};
interface FileDraggableComponentProps {
  sectionIndex: number;
  rowIndex: number;
  rowSortingIndex: number | null;
  index: number;
  grid: number;
  formField?: ISection["rows"][0]["fields"][0];
  label?: string;
  handleVisibilityChange?: (sectionIndex: number, rowIndex: number, index: number, update: boolean) => void;
  handleAddEmptyRow?: (sectionIndex: number, rowIndex: number, isAddAbove: boolean) => void;
}
const FileDraggableComponent: FC<FileDraggableComponentProps> = ({
  sectionIndex,
  rowIndex,
  rowSortingIndex,
  index,
  grid,
  formField,
  label,
  handleVisibilityChange,
  handleAddEmptyRow
}) => {
  const droppableIndexStr = getFileDropLocationId(rowSortingIndex, formField?.id ?? null);
  const draggableIndexStr = formField ? droppableIndexStr : `file-placeholder-${JSON.stringify([rowSortingIndex, index])}`;
  return <Droppable droppableId={droppableIndexStr} key={droppableIndexStr} index={rowIndex} data-sentry-element="Droppable" data-sentry-component="FileDraggableComponent" data-sentry-source-file="file-display-settings-dialog.tsx">
			{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    @ts-ignore */}
			{droppableProvided => <Grid item xs={12} md={grid} sx={{
      alignContent: "center"
    }} ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
					{droppableProvided.placeholder}
					<Draggable key={draggableIndexStr} draggableId={draggableIndexStr} isDragDisabled={!formField} index={0}>
						{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
       @ts-ignore */}
						{(draggableProvided, snapshot) => <div ref={draggableProvided.innerRef} {...draggableProvided.draggableProps} {...draggableProvided.dragHandleProps}>
								<FileSettingCard sortingIndex={formField?.sortingIndex} form={formField} label={label} draggableId={draggableIndexStr} droppableId={droppableIndexStr} dragging={snapshot.isDragging} dragOver={snapshot.draggingOver} sectionIndex={sectionIndex} rowIndex={rowIndex} index={index} handleVisibilityChange={handleVisibilityChange} handleAddEmptyRow={handleAddEmptyRow} />
							</div>}
					</Draggable>
				</Grid>}
		</Droppable>;
};
interface FileSettingCardProps {
  draggableId: string | null | undefined;
  droppableId: string | null | undefined;
  dragging: boolean;
  dragOver: string | null | undefined;
  sortingIndex?: number;
  form?: ISection["rows"][0]["fields"][0];
  sectionIndex: number;
  rowIndex: number;
  index: number;
  label?: string;
  handleVisibilityChange?: (sectionIndex: number, rowIndex: number, index: number, update: boolean) => void;
  handleAddEmptyRow?: (sectionIndex: number, rowIndex: number, isAddAbove: boolean) => void;
}
const FileSettingCard: FC<FileSettingCardProps> = ({
  sortingIndex,
  form,
  dragging,
  draggableId,
  dragOver,
  sectionIndex,
  rowIndex,
  index,
  label,
  handleVisibilityChange,
  handleAddEmptyRow
}) => {
  const popover = usePopover<HTMLButtonElement>();
  return <Card key={sortingIndex} elevation={dragging ? 8 : 1} sx={{
    backgroundColor: theme => theme.palette.mode === "dark" ? "neutral.800" : "background.paper",
    ...(dragging && {
      backgroundColor: theme => theme.palette.mode === "dark" ? "neutral.800" : "background.paper"
    }),
    m: 1,
    userSelect: "none",
    "&:hover": {
      backgroundColor: theme => theme.palette.mode === "dark" ? "neutral.700" : "neutral.50"
    },
    "&.MuiPaper-elevation1": {
      boxShadow: 1
    },
    borderRadius: 1,
    filter: draggableId === dragOver ? "blur(2px)" : "none"
  }} data-sentry-element="Card" data-sentry-component="FileSettingCard" data-sentry-source-file="file-display-settings-dialog.tsx">
			<CardHeader subheader={form ? label ?? fromKababCaseToTitleCase(form.id) : "+"} sx={{
      pt: 2,
      textAlign: form ? "left" : "center"
    }} ref={popover.anchorRef} onClick={() => {
      if (!handleAddEmptyRow) return;
      void popover.handleToggle();
    }} action={handleVisibilityChange !== undefined && <>
							{form?.isVisible ? <IconButton aria-label="view" onClick={() => void handleVisibilityChange(sectionIndex, rowIndex, index, false)}>
									<Visibility />
								</IconButton> : <IconButton aria-label="view" onClick={() => void handleVisibilityChange(sectionIndex, rowIndex, index, true)}>
									<VisibilityOff />
								</IconButton>}
						</>} data-sentry-element="CardHeader" data-sentry-source-file="file-display-settings-dialog.tsx" />
			<Popover anchorOrigin={{
      horizontal: "left",
      vertical: "bottom"
    }} transformOrigin={{
      horizontal: "left",
      vertical: "top"
    }} anchorEl={popover.anchorRef.current} onClose={popover.handleClose} open={popover.open} data-sentry-element="Popover" data-sentry-source-file="file-display-settings-dialog.tsx">
				<Stack spacing={3} sx={{
        p: 3
      }} data-sentry-element="Stack" data-sentry-source-file="file-display-settings-dialog.tsx">
					<Stack direction="column" spacing={3} data-sentry-element="Stack" data-sentry-source-file="file-display-settings-dialog.tsx">
						<Button variant="outlined" onClick={() => {
            if (!handleAddEmptyRow) return;
            void handleAddEmptyRow(sectionIndex, rowIndex, true);
            void popover.handleClose();
          }} data-sentry-element="Button" data-sentry-source-file="file-display-settings-dialog.tsx">
							Add Empty Row Above
						</Button>
						<Button variant="outlined" onClick={() => {
            if (!handleAddEmptyRow) return;
            void handleAddEmptyRow(sectionIndex, rowIndex, false);
            void popover.handleClose();
          }} data-sentry-element="Button" data-sentry-source-file="file-display-settings-dialog.tsx">
							Add Empty Row Below
						</Button>
						<Button color="inherit" onClick={popover.handleClose} data-sentry-element="Button" data-sentry-source-file="file-display-settings-dialog.tsx">
							Cancel
						</Button>
					</Stack>
				</Stack>
			</Popover>
		</Card>;
};